import {Injectable} from '@angular/core';
import {Logger} from '@mydocma/common/src/lib/logger';
import { firstValueFrom } from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {FaultDuplicateWizardDataService} from './fault-duplicate-wizard.data.service';
import {FaultDuplicateWizardStepperService} from './fault-duplicate-wizard-stepper.service';
import { NotificationService } from '@mydocma/component-library/src/lib/notification';
import {MmFilterService} from '../../../mm-filter/mm-filter.service';
import {ViewFault} from '@docma-mm-core/fault/fault';

@Injectable()
export class FaultDuplicateWizardViewService {

  constructor(
    private readonly logger: Logger,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private dataService: FaultDuplicateWizardDataService,
    private stepperService: FaultDuplicateWizardStepperService,
    private filterService: MmFilterService
  ) {}

  async executeDuplication(): Promise<boolean> {
    try {
      const validationResult = await this.validateDuplicationState();
      if (!validationResult.isValid) {
        await this.showErrorNotification(validationResult.errorMessage);
        return false;
      }

      const state = this.stepperService.getCompleteData();
      const payload = {
        originalFaultId: state.originalFault?.data?.Id,
        projectUid: state.project?.MyDocmaUid,
        duplicateType: state.optionsStep.duplicateType,
        duplicatesToCreate: state.optionsStep.duplicatesToCreate,
        targetStructures: state.locationStep.selectedStructures?.map(s => ({ Id: s.Id, Uid: s.Uid })) ?? [],
        selectedAttachmentUids: state.attachmentStep.selectedAttachments?.map(a => a.uid) ?? []
      };

      const result = await this.dataService.duplicateFaultsFromCurrentState();

      if (result.success) {
        await this.showSuccessNotification(result.createdFaultObjects.length);

        if (result.faultsWithTrimmedAgNumber && result.faultsWithTrimmedAgNumber.length > 0) {
          await this.showTrimmedAgNumberNotification(result.faultsWithTrimmedAgNumber);
        }

        if (result.faultsNotAddedToPools && result.faultsNotAddedToPools.length > 0) {
          await this.showNotAddedToPoolsNotification(result.faultsNotAddedToPools.length);
        }
       
        // redundant check for active filters, handled elsewhere
        // try {
        //   const hasActiveFilters = await this.checkActiveFilters();
        //   if (hasActiveFilters) {
        //     this.notificationService.info('Some duplicated faults may be hidden due to active filters');
        //   }
        // } catch (error) {
        //   console.warn('[Duplicate Faults] Filter check failed:', error);
        // }
        
        return true;
      } else {
        await this.showDuplicationErrors(result.errors, state);
        return false;
      }

    } catch (error) {
      this.logger.error(error, '', 'FaultDuplicateWizardViewService');
      await this.showErrorNotification('MM.fault.duplicate.error.unexpected');
      return false;
    }
  }

  async validateDuplicationState(): Promise<{isValid: boolean, errorMessage?: string}> {
    const state = this.stepperService.getCompleteData();
    const fault = state.originalFault;
    
    if (!fault) {
      return {
        isValid: false,
        errorMessage: 'MM.fault.duplicate.error.noFault'
      };
    }

    if (!state.optionsStep.duplicateType) {
      return {
        isValid: false,
        errorMessage: 'MM.fault.duplicate.error.noDuplicateType'
      };
    }

    if (state.optionsStep.duplicateType === 'single' && !state.optionsStep.duplicatesToCreate) {
      return {
        isValid: false,
        errorMessage: 'MM.fault.duplicate.error.noDuplicateCount'
      };
    }

    if (!state.locationStep.selectedStructures || state.locationStep.selectedStructures.length === 0) {
      return {
        isValid: false,
        errorMessage: 'MM.fault.duplicate.error.noStructuresSelected'
      };
    }

    if (state.optionsStep.duplicateType === 'single' && state.locationStep.selectedStructures.length !== 1) {
      return {
        isValid: false,
        errorMessage: 'MM.fault.duplicate.error.singleLocationRequired'
      };
    }

    if (state.optionsStep.duplicateType === 'multiple' && state.locationStep.selectedStructures.length < 1) {
      return {
        isValid: false,
        errorMessage: 'MM.fault.duplicate.error.multipleLocationsRequired'
      };
    }

    return {isValid: true};
  }


  private async showSuccessNotification(count: number): Promise<void> {
    const translationKey = count === 1 ? 'MM.fault.duplicate.successSingular' : 'MM.fault.duplicate.successPlural';
    const message = await this.translateService.get(translationKey).toPromise();
    this.notificationService.success(message);
  }

  private async showTrimmedAgNumberNotification(trimmedFaults: ViewFault[]): Promise<void> {
    const faultIds = trimmedFaults
      .map(f => f.data?.Number1 || f.uid)
      .filter(id => id)
      .join(', ');

    const message = await this.translateService.get('MM.fault.duplicate.agNumberTrimmed', {
      count: trimmedFaults.length,
      ids: faultIds
    }).toPromise();

    this.notificationService.info(message);
  }

  private async showNotAddedToPoolsNotification(count: number): Promise<void> {
    const message = await this.translateService.get('MM.fault.creation.successWithoutPoolFiltered').toPromise();
    this.notificationService.info(message);
  }

  async showErrorNotification(translationKey: string): Promise<void> {
    const message = await this.translateService.get(translationKey).toPromise();
    this.logger.error(message, '', 'FaultDuplicateWizardViewService');
    this.notificationService.error(message);
  }

  private async showDuplicationErrors(errors: string[], state: any): Promise<void> {
    let expectedDuplicates = 1;
    if (state.optionsStep.duplicateType === 'single' && state.optionsStep.duplicatesToCreate) {
      expectedDuplicates = state.optionsStep.duplicatesToCreate;
    } else if (state.optionsStep.duplicateType === 'multiple' && state.locationStep.selectedStructures) {
      expectedDuplicates = state.locationStep.selectedStructures.length;
    }

    const translationKey = expectedDuplicates === 1 ? 'MM.fault.duplicate.errorSingular' : 'MM.fault.duplicate.errorPlural';
    const message = await this.translateService.get(translationKey).toPromise();
    this.logger.error(errors?.join(', '), '', 'FaultDuplicateWizardViewService');
    this.notificationService.error(message);
  }

  async validateAttachmentStep(): Promise<{isValid: boolean, errorMessage?: string}> {
    const state = this.stepperService.getCompleteData();
    
    if (state.attachmentStep.selectedAttachments) {
      const invalidAttachments = state.attachmentStep.selectedAttachments.filter(att => !att || !att.uid);
      if (invalidAttachments.length > 0) {
        return {
          isValid: false,
          errorMessage: 'MM.fault.duplicate.error.invalidAttachments'
        };
      }
    }

    return {isValid: true};
  }

  async showAttachmentValidationError(errorKey: string): Promise<void> {
    await this.showErrorNotification(errorKey);
  }
  
  private async checkActiveFilters(): Promise<boolean> {
    const filterState = await firstValueFrom(this.filterService.state);
    return !!(
      filterState?.fieldFilters?.length || 
      filterState?.poolInclusionFilters?.length || 
      filterState?.poolExclusionFilters?.length
    );
  }
  


  shouldShowPlanSections(): boolean {
    const state = this.stepperService.getCompleteData();
    const fault = state.originalFault;
    
    if (!fault?.data?.Structure || !state.locationStep.selectedStructures) {
      return false;
    }

    const originalStructure = fault.data.Structure;
    return this.dataService.canDuplicatePlanSections(originalStructure, state.locationStep.selectedStructures);
  }
}